import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import moment from 'moment-timezone';
import { googleAccountMatches, insertPersonalEvent, deletePersonalEvent } from './personalCalendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { collection, onSnapshot, doc, query, where, getDoc } from 'firebase/firestore';
import { bookingAction, loadBookingSchedule, newRequestId, getPendingBooking, checkPendingBooking, retryPendingBooking } from './bookingApi';
import { db, auth, googleProvider } from './firebase';
import { gapi } from "gapi-script";
import { onAuthStateChanged } from "firebase/auth";
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
} from 'firebase/auth';
import './App.css';
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import ru from "date-fns/locale/ru";
import enUS from "date-fns/locale/en-US";
import googleLogo from './google.png';
import translations from './i18n/translations';
import { RULES_VERSION, needsRulesAcceptance } from './config/rulesGate';
import {
  isSlotUnavailable,
  isDayUnavailable,
  normalizeOpenHours,
} from './config/availability';

const CLIENT_ID = "308971275577-vmrg6kdkcfp0676e7ajg9gt39f1p2hfk.apps.googleusercontent.com";
const API_KEY = "AIzaSyDtdrSo2QhsXabGgUP2HJLbkZ9BOTqsM80";
const SCOPES = "https://www.googleapis.com/auth/calendar.events";

// useT использует useLocation — реактивен при смене маршрута /#/ ↔ /#/en
function useT() {
  const location = useLocation();
  const isEN = location.pathname === '/en';
  const strings = translations[isEN ? 'en' : 'ru'];
  const t = (path) => path.split('.').reduce((obj, key) => obj?.[key], strings) || path;
  return { t, isEN };
}

// getT для компонентов вне хука (принимает isEN пропом)
function getT(isEN) {
  const strings = translations[isEN ? 'en' : 'ru'];
  return (path) => path.split('.').reduce((obj, key) => obj?.[key], strings) || path;
}



function PaymentBadge({ paid, isEN }) {
  const t = getT(isEN);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
      backgroundColor: paid ? '#d4edda' : '#fff3cd',
      color: paid ? '#155724' : '#856404',
      border: `1px solid ${paid ? '#c3e6cb' : '#ffeeba'}`,
    }}>
      {paid ? t('lessons.paid') : t('lessons.unpaid')}
    </span>
  );
}

function CustomAlertModal({ show, message, onClose, okText }) {
  if (!show) return null;
  const text = message.toLowerCase();
  const icon =
    (text.includes('успешно') || text.includes('оплачено') || text.includes('добавлены') ||
     text.includes('successfully') || text.includes('booked') || text.includes('paid lessons')) ? '🎉' :
    (text.includes('ошибка') || text.includes('нельзя') || text.includes('недоступно') ||
     text.includes('error') || text.includes('cannot') || text.includes('unavailable') || text.includes('log in to')) ? '🧐' :
    (text.includes('удалён') || text.includes('deleted')) ? '🗓️' :
    (text.includes('подтвердите') || text.includes('verify')) ? '☝️' : '👩‍🏫';

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(3px)',
      display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100,
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        backgroundColor: '#fff', borderRadius: '16px', padding: '30px',
        width: '90%', maxWidth: '400px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
        textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px',
      }}>
        <div style={{
          width: '60px', height: '60px', borderRadius: '50%', backgroundColor: '#e8f4fd',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '30px',
        }}>{icon}</div>
        <div style={{ fontSize: '16px', color: '#34495e', lineHeight: '1.5', whiteSpace: 'pre-line' }}>{message}</div>
        <button onClick={onClose} style={{
          padding: '12px 30px', backgroundColor: '#3498db', color: 'white',
          border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600',
          cursor: 'pointer', minWidth: '120px',
        }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#2980b9'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#3498db'}
        >{okText}</button>
      </div>
    </div>
  );
}

function CustomConfirmModal({ show, message, onConfirm, onCancel, yesText, cancelText }) {
  if (!show) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)',
      display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1101,
    }} onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} style={{
        backgroundColor: '#fff', borderRadius: '16px', padding: '30px',
        width: '90%', maxWidth: '400px', boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
        textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px',
      }}>
        <div style={{
          width: '60px', height: '60px', borderRadius: '50%', backgroundColor: '#fff3cd',
          color: '#d35400', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '28px', fontWeight: 'bold', fontFamily: 'serif',
        }}>¿?</div>
        <div style={{ fontSize: '17px', color: '#2c3e50', fontWeight: '500', lineHeight: '1.5', whiteSpace: 'pre-line' }}>{message}</div>
        <div style={{ display: 'flex', gap: '15px', width: '100%', justifyContent: 'center' }}>
          <button onClick={onCancel} style={{
            padding: '12px 24px', backgroundColor: '#ecf0f1', color: '#7f8c8d',
            border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer', flex: 1,
          }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#dde1e3'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#ecf0f1'}
          >{cancelText}</button>
          <button onClick={onConfirm} style={{
            padding: '12px 24px', backgroundColor: '#27ae60', color: 'white',
            border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer', flex: 1,
          }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#219150'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#27ae60'}
          >{yesText}</button>
        </div>
      </div>
    </div>
  );
}

// Модалка правил переноса. Рендерится по условию (а не через проп show),
// чтобы чекбокс сбрасывался при каждом открытии.
function RulesModal({ t, onConfirm, onCancel, saving }) {
  const [checked, setChecked] = useState(false);
  const items = [t('rules.rulesItem1'), t('rules.rulesItem2')];

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)',
      display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1102,
    }} onClick={saving ? undefined : onCancel}>
      <div onClick={(e) => e.stopPropagation()} style={{
        backgroundColor: '#fff', borderRadius: '16px', padding: '30px',
        width: '90%', maxWidth: '420px', maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
        textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '18px',
      }}>
        <div style={{
          width: '60px', height: '60px', borderRadius: '50%', backgroundColor: '#e8f4fd',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '30px', flexShrink: 0,
        }}>👩‍🏫</div>

        <div style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50', lineHeight: '1.4' }}>
          {t('rules.rulesTitle')}
        </div>

        <ul style={{ listStyle: 'none', padding: 0, margin: 0, textAlign: 'left', width: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {items.map((item, idx) => (
            <li key={idx} style={{
              display: 'flex', gap: '10px', alignItems: 'flex-start',
              fontSize: '15px', color: '#34495e', lineHeight: '1.5',
            }}>
              <span style={{ color: '#27ae60', fontWeight: 'bold', flexShrink: 0 }}>•</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <label style={{
          display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
          padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px',
          cursor: saving ? 'default' : 'pointer', fontSize: '15px', color: '#2c3e50', fontWeight: '500',
        }}>
          <input type="checkbox" checked={checked} disabled={saving}
            onChange={(e) => setChecked(e.target.checked)}
            style={{ width: '18px', height: '18px', cursor: saving ? 'default' : 'pointer', flexShrink: 0 }} />
          <span>{t('rules.rulesCheckbox')}</span>
        </label>

        <div style={{ display: 'flex', gap: '15px', width: '100%', justifyContent: 'center' }}>
          <button onClick={onCancel} disabled={saving} style={{
            padding: '12px 24px', backgroundColor: '#ecf0f1', color: '#7f8c8d',
            border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600',
            cursor: saving ? 'not-allowed' : 'pointer', flex: 1,
          }}>{t('rules.rulesLater')}</button>
          <button onClick={onConfirm} disabled={!checked || saving} style={{
            padding: '12px 24px', backgroundColor: (!checked || saving) ? '#ccc' : '#27ae60', color: 'white',
            border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600',
            cursor: (!checked || saving) ? 'not-allowed' : 'pointer', flex: 1,
          }}>{t('rules.rulesConfirm')}</button>
        </div>
      </div>
    </div>
  );
}

function BookingCalendar() {
  const { t, isEN } = useT();
  const dateLocale = isEN ? enUS : ru;

  const calendarLocalizer = useMemo(() => dateFnsLocalizer({
    format,
    parse,
    startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
    getDay,
    locales: isEN ? { 'en-US': enUS } : { ru },
  }), [isEN]);

  const [recentLessons, setRecentLessons] = useState([]);

  const [myLessons, setMyLessons] = useState([]);
  const [partnerLessons, setPartnerLessons] = useState([]);
  // events = свежие/будущие уроки (за последний месяц — для брони и истории) +
  // ВСЕ неоплаченные уроки любой давности (чтобы оплата с баланса не сломалась).
  // Старые оплаченные уроки не грузим — это и есть экономия чтений Firestore.
  const events = useMemo(() => {
    const byId = new Map();
    for (const e of recentLessons) byId.set(e.id, e);

    for (const e of myLessons) byId.set(e.id, e);
    for (const e of partnerLessons) byId.set(e.id, e);
    return Array.from(byId.values());
  }, [recentLessons, myLessons, partnerLessons]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [lessonDuration, setLessonDuration] = useState(60);
  const [user, setUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [authMode, setAuthMode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showSelectedModal, setShowSelectedModal] = useState(false);
  // Пока Firebase не восстановил сессию из хранилища, шапку с кнопкой входа не
  // показываем — иначе на каждой загрузке мелькает «вы не вошли».
  const [authReady, setAuthReady] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const googleInitRef = useRef(null);
  const firebaseEmailRef = useRef(null);
  const googleSyncRef = useRef(false);
  const googleRetryRef = useRef(null);
  const [googleSyncing, setGoogleSyncing] = useState(false);
  const [googleSyncPending, setGoogleSyncPending] = useState(false);
  const googleSyncPendingRef = useRef(false);
  googleSyncPendingRef.current = googleSyncPending;
  const [calendarFresh, setCalendarFresh] = useState(false);
  const bookingWindowEndRef = useRef(null);
  const googleLessonsRef = useRef([]);
  const [wallet, setWallet] = useState(null);
  const [rulesRequired, setRulesRequired] = useState(false);
  const [scheduleReady, setScheduleReady] = useState(false);
  const [bookingSaving, setBookingSaving] = useState(false);
  const [paymentReported, setPaymentReported] = useState(false);
  const bookingSavingRef = useRef(false);
  const bookingRequestRef = useRef(null);
  const [pendingBooking, setPendingBooking] = useState(null);
  const [checkingBooking, setCheckingBooking] = useState(false);
  const recoveryRunningRef = useRef(false);
  const sessionRef = useRef(null);
  const scheduleGenerationRef = useRef(0);
  const [scheduleError, setScheduleError] = useState(false);

  const [blockedSlots, setBlockedSlots] = useState([]);
  const blockedSlotsRef = useRef([]);
  // Часы, открытые админом сверх обычного расписания (коллекция openHours).
  const [openHours, setOpenHours] = useState([]);
  const openHoursRef = useRef([]);
  const [bookingAccess, setBookingAccess] = useState(null);
  const bookingAccessRef = useRef(null);
  const [bookingBlockedUntil, setBookingBlockedUntil] = useState(null);
  const [showPastLessons, setShowPastLessons] = useState(false);

  const [customAlert, setCustomAlert] = useState({ show: false, message: '' });
  const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
  const [rulesModal, setRulesModal] = useState({ show: false, saving: false });
  // Кэш на сессию: если согласие уже подтверждено, не читаем документ при каждой брони.
  const rulesAcceptedRef = useRef(false);

  const triggerAlert = (message) => setCustomAlert({ show: true, message });
  const closeCustomAlert = () => setCustomAlert({ show: false, message: '' });
  const triggerConfirm = (message, onConfirmAction) =>
    setConfirmModal({ show: true, message, onConfirm: onConfirmAction });
  const closeConfirmModal = () => setConfirmModal({ show: false, message: '', onConfirm: null });
  const handleConfirmAction = () => {
    if (confirmModal.onConfirm) confirmModal.onConfirm();
    closeConfirmModal();
  };

  const isMobile = window.innerWidth <= 768;
  const defaultView = isMobile ? 'day' : 'week';

  const initGoogleAPI = async () => {
    if (googleInitRef.current) return googleInitRef.current;
    googleInitRef.current = new Promise((resolve, reject) => {
      gapi.load("client:auth2", async () => {
        try {
          await gapi.client.init({
            apiKey: API_KEY,
            clientId: CLIENT_ID,
            discoveryDocs: ["https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest"],
            scope: SCOPES,
          });
          const authInstance = gapi.auth2.getAuthInstance();
          const changed = () => setIsAuthorized(googleAccountMatches(authInstance, firebaseEmailRef.current));
          changed();
          authInstance.isSignedIn.listen(changed);
          resolve();
        } catch (error) {
          console.error("Google API init error:", error);
          reject(error);
        }
      });
    });
    try { return await googleInitRef.current; }
    catch (error) { googleInitRef.current = null; throw error; }
  };


  const refreshSchedule = useCallback(async () => {
    const generation = scheduleGenerationRef.current;
    const session = sessionRef.current;
    if (!session) return;
    setScheduleError(false);
    try {
      const data = await loadBookingSchedule();
      if (generation !== scheduleGenerationRef.current || session !== sessionRef.current) return;
      const toEvent = d => ({ ...d, start: new Date(d.start), end: new Date(d.end) });
      setRecentLessons(data.busy.map(toEvent));
      setBlockedSlots(data.blockedSlots.map(toEvent));
      blockedSlotsRef.current = data.blockedSlots.map(toEvent);
      setOpenHours(normalizeOpenHours(data.openHours));
      openHoursRef.current = data.openHours;
      setWallet(data.wallet);
      setPartnerLessons(data.partnerLessons.map(toEvent));
      setRulesRequired(data.rulesRequired);
      setCalendarFresh(data.calendarFresh !== false);
      bookingWindowEndRef.current = data.bookingWindowEnd ? Date.parse(data.bookingWindowEnd) : null;
      rulesAcceptedRef.current = data.rulesAcceptedVersion >= RULES_VERSION;
      setScheduleReady(true);
    } catch (error) {
      if (generation !== scheduleGenerationRef.current || session !== sessionRef.current) return;
      setScheduleReady(false);
      setScheduleError(error.code?.startsWith('booking/') && typeof error.message === 'string' && error.message.trim()
        ? error.message : true);
      setRecentLessons([]); setBlockedSlots([]); setPartnerLessons([]); setWallet(null);
      blockedSlotsRef.current = [];
      console.error('Schedule unavailable:', { code: error.code, message: error.message, status: error.status });
    }
  }, []);

  useEffect(() => {
    scheduleGenerationRef.current++;
    setSelectedSlots([]);
    bookingRequestRef.current = null;
    setScheduleReady(false);
    setRecentLessons([]); setBlockedSlots([]); setPartnerLessons([]); setWallet(null); setOpenHours([]);
    blockedSlotsRef.current = []; openHoursRef.current = [];
    if (!user?.emailVerified || !bookingAccess) return undefined;
    let active = true, running = false, again = false;
    const refresh = async () => {
      if (running) { again = true; return; }
      do {
        again = false; running = true;
        if (active) await refreshSchedule();
      } while (active && again);
      running = false;
    };
    // Load once. Cached and initial revision snapshots must not each reread
    // the schedule. Hidden tabs wait until the student returns.
    refresh();
    let revision;
    const unsubscribe = onSnapshot(doc(db, '_bookingState', 'global'), snapshot => {
      if (snapshot.metadata?.fromCache) return;
      const next = snapshot.data?.()?.version;
      if (revision !== undefined && next !== revision && document.visibilityState !== 'hidden') refresh();
      revision = next;
    }, () => setScheduleReady(false));
    // Google events can change independently of Firestore. Refresh when the
    // student returns to the tab; every booking also checks Google on the server.
    const visibleRefresh = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', visibleRefresh);
    return () => {
      active = false; unsubscribe();
      document.removeEventListener('visibilitychange', visibleRefresh);
    };
  }, [user?.uid, user?.emailVerified, bookingAccess, refreshSchedule]);

  useEffect(() => {
    setPendingBooking(getPendingBooking());
    if (!user?.emailVerified || !getPendingBooking()) return undefined;
    let active = true;
    checkPendingBooking().then(result => {
      if (!active) return;
      setPendingBooking(getPendingBooking());
      if (result) {
        setCustomAlert({ show: true, message: t('alerts.bookingSuccess') });
        refreshSchedule();
      }
    }).catch(() => { /* Keep the unresolved request visible and retryable. */ });
    return () => { active = false; };
    // Recovery is per Firebase account, independent of the selected UI language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, user?.emailVerified, refreshSchedule]);

  const recoverBooking = async (retry = false) => {
    if (recoveryRunningRef.current) return;
    recoveryRunningRef.current = true; setCheckingBooking(true);
    try {
      const result = retry ? await retryPendingBooking() : await checkPendingBooking();
      setPendingBooking(getPendingBooking());
      if (result) { triggerAlert(t('alerts.bookingSuccess')); setSelectedSlots([]); await refreshSchedule(); }
      else triggerAlert(t('alerts.bookingUnconfirmed'));
    } catch (error) {
      setPendingBooking(getPendingBooking());
      triggerAlert(error.message || t('alerts.bookingUnconfirmed'));
    } finally { recoveryRunningRef.current = false; setCheckingBooking(false); }
  };

  const ensureGoogleAccount = async (u, interactive = false) => {
    await initGoogleAPI();
    const auth2 = gapi.auth2.getAuthInstance();
    if (!googleAccountMatches(auth2, u.email) && interactive)
      await auth2.signIn({ scope: SCOPES, login_hint: u.email, prompt: 'select_account' });
    if (firebaseEmailRef.current !== u.email || !googleAccountMatches(auth2, u.email)) {
      setIsAuthorized(false);
      throw Error(isEN ? 'Choose the same Google account you use to log in.' : 'Выберите в Google тот же аккаунт, которым вошли на сайт.');
    }
    const cu = auth2.currentUser.get();
    if (!cu.hasGrantedScopes(SCOPES)) {
      if (!interactive) throw Error(t('alerts.googleAuthError'));
      await cu.grant({ scope: SCOPES });
      if (!cu.hasGrantedScopes(SCOPES)) throw Error(t('alerts.googleAuthError'));
    }
    setIsAuthorized(true);
  };
  const syncPersonalLesson = async (lesson, u) => {
    await ensureGoogleAccount(u);
    const prepared = await bookingAction('prepareGoogleEvent', { lessonId: lesson.id });
    const googleEventId = prepared.googleEventId;
    if (!googleEventId) throw Error(t('alerts.googleAuthError'));
    let synced = false;
    try {
      await ensureGoogleAccount(u);
      await insertPersonalEvent(gapi, { googleEventId, lessonId: lesson.id, email: u.email,
        start: new Date(lesson.start), end: new Date(lesson.end) });
      synced = true;
    } finally {
      // Reopens deletion if an insert completed after the lesson was cancelled.
      await bookingAction('setGoogleEvent', { lessonId: lesson.id, googleEventId, synced });
    }
  };
  const drainGoogleDeletes = async u => {
    const result = await bookingAction('studentCalendarTasks');
    for (const task of result.tasks || []) {
      await ensureGoogleAccount(u);
      await deletePersonalEvent(gapi, task.googleEventId);
      await bookingAction('finishCalendarTask', { taskId: task.id, version: task.version });
    }
    if (result.hasMore) throw Error(t('alerts.googlePending').trim());
  };


  const credits = wallet;
  const getPartnerInfo = () => credits?.partnerEmail
    ? { userEmail: credits.partnerEmail, userName: partnerLessons[0]?.userName || credits.partnerEmail }
    : null;
  const getSharedBalance = () => credits ? credits.purchased - credits.booked : 0;

  const nowDate = new Date();

  const selectedSlotEvents = selectedSlots.map((slot, idx) => ({
    id: `temp-${idx}`, title: '', start: slot.start,
    end: new Date(slot.start.getTime() + lessonDuration * 60000), temp: true,
  }));
  const combinedEvents = [...events, ...blockedSlots, ...selectedSlotEvents];

  // ── Правила переноса ──────────────────────────────────────────────────────
  // Требование согласия приходит из личного профиля; список учеников не входит в bundle.
  // Показываем модалку, только если согласие
  // не записано или записано для более старой версии правил.
  const shouldShowRules = async () => {
    if (!needsRulesAcceptance(rulesRequired)) return false;
    if (rulesAcceptedRef.current) return false;
    try {
      const snap = await getDoc(doc(db, 'users', user.email));
      const accepted = snap.exists() ? snap.data().rulesAcceptedVersion : undefined;
      if (typeof accepted === 'number' && accepted >= RULES_VERSION) {
        rulesAcceptedRef.current = true;
        return false;
      }
      return true;
    } catch (e) {
      // Не смогли проверить — лучше показать правила лишний раз, чем ни разу.
      console.warn('Не удалось прочитать согласие с правилами:', e.message);
      return true;
    }
  };

  const handleRulesConfirm = async () => {
    setRulesModal({ show: true, saving: true });
    try {
      await bookingAction('acceptRules');
      rulesAcceptedRef.current = true;
    } catch (e) {
      triggerAlert(e.message || t('alerts.bookingError'));
      setRulesModal({ show: true, saving: false });
      return;
    }
    setRulesModal({ show: false, saving: false });
    await createBookings();
  };

  // ── Серверное бронирование ──────────────────────────────────
  // Гейт правил: до создания брони. «Не сейчас» — бронь не создаётся.
  const handleBooking = async () => {
    if (!user) { triggerAlert(t('alerts.notAuthorized')); return; }
    if (await shouldShowRules()) { setRulesModal({ show: true, saving: false }); return; }
    await createBookings();
  };

  // Само создание броней вынесено отдельно, чтобы после согласия с правилами
  // продолжить тот же флоу без повторного клика по слоту.

  const createBookings = async () => {
    if (!user || !bookingAccess || !scheduleReady || bookingSavingRef.current || pendingBooking) return;
    if (!selectedSlots.length || !calendarFresh) return;
    if (bookingNeedsPayment && !paymentReported) return;
    bookingSavingRef.current = true; setBookingSaving(true);
    const slots = selectedSlots
      .map(slot => ({ start: slot.start.toISOString(), duration: lessonDuration }))
      .sort((a, b) => a.start.localeCompare(b.start));
    const fingerprint = JSON.stringify({ slots, paymentReported });
    if (bookingRequestRef.current?.fingerprint !== fingerprint)
      bookingRequestRef.current = { fingerprint, id: newRequestId() };
    try {
      const result = await bookingAction('book', {
        slots, paymentReported, studentBooking: true, userEmail: user.email, userName: user.displayName || user.email,
        userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }, bookingRequestRef.current.id);
      // Tell the student as soon as the server confirms its commit. Google
      // synchronization can finish later and cannot revoke this booking.
      triggerAlert(t('alerts.bookingSuccess'));
      bookingRequestRef.current = null; setSelectedSlots([]); setPendingBooking(null);
      const createdLessons = Array.isArray(result.lessons) ? result.lessons : slots.map((slot, i) => ({
        id: result.lessonIds[i], start: slot.start,
        end: new Date(Date.parse(slot.start) + slot.duration * 60000).toISOString(),
      }));
      // Keep the server-confirmed lessons visible even if the realtime read
      // connection hits a quota/network error immediately after the commit.
      const paidStarts = new Set(slots.slice(0, result.paid || 0).map(slot => slot.start));
      setMyLessons(current => {
        const saved = new Map(current.map(lesson => [lesson.id, lesson]));
        createdLessons.forEach(lesson => saved.set(lesson.id, { ...lesson, userEmail: user.email,
          userName: user.displayName || user.email, title: 'Урок', paid: paidStarts.has(lesson.start),
          start: new Date(lesson.start), end: new Date(lesson.end) }));
        return [...saved.values()];
      });
      // Database commit is authoritative. Google errors never claim success.
      let pendingGoogle = false;
      if (isAuthorized) {
        // Use each saved lesson's own ID and interval. Chronological slots keep
        // compatibility with responses saved before the API included lessons.
        for (const lesson of createdLessons) {
          try {
            await syncPersonalLesson(lesson, user);
          } catch (error) { pendingGoogle = true; console.error('Student calendar sync failed:', error.code); }
        }
      }
      setGoogleSyncPending(pendingGoogle);
      triggerAlert(t('alerts.bookingSuccess') + (pendingGoogle ? t('alerts.googlePending') : ''));
      await refreshSchedule();
    } catch (error) {
      const pending = getPendingBooking(); setPendingBooking(pending);
      triggerAlert(pending ? t('alerts.bookingUnconfirmed') : (error.message || t('alerts.bookingError')));
      await refreshSchedule();
    } finally { bookingSavingRef.current = false; setBookingSaving(false); }
  };

  // ── Серверная оплата с баланса ───────────────────────────────

  const handlePayFromBalance = async () => {
    const count = Math.min(getSharedBalance(), pendingLessons);
    if (count <= 0) return;
    const requestId = newRequestId();
    triggerConfirm(t('confirm.payCredits')(count), async () => {
      try {
        const result = await bookingAction('payBalance', {}, requestId);
        triggerAlert(t('alerts.paidSuccess') + result.paid);
        await refreshSchedule();
      } catch (error) { triggerAlert(error.message || t('alerts.payError')); }
    });
  };

  // ── Серверная отмена и возврат кредита ────────────────────────────────

  const handleSlotDelete = async (event) => {
    if ((new Date(event.start) - new Date()) / 3600000 < 24) { triggerAlert(t('alerts.cancelWarning')); return; }
    const requestId = newRequestId();
    triggerConfirm(
      t('confirm.deleteLesson') + ' ' + format(event.start, 'dd MMMM yyyy, HH:mm', { locale: dateLocale }) + '?',
      async () => {
        try {
          await bookingAction('cancel', { lessonId: event.id }, requestId);
          let pendingGoogle = false;
          if (isAuthorized) {
            try { await drainGoogleDeletes(user); }
            catch { pendingGoogle = true; setGoogleSyncPending(true); }
          }
          triggerAlert(t('alerts.deleted') + (pendingGoogle ? t('alerts.googlePending') : ''));
          await refreshSchedule();
        } catch (error) { triggerAlert(error.message || t('alerts.deleteError')); }
      }
    );
  };

  // ── DB: синхронизация Google Calendar ────────────────────────────────────
  const handleGoogleAuth = async () => {
    if (googleSyncRef.current) return;
    googleSyncRef.current = true; setGoogleSyncing(true);
    try {
      await ensureGoogleAccount(user, true);
      await drainGoogleDeletes(user);
      for (const lesson of userBookedLessons) {
        if (!lesson.googleEventId || lesson.studentCalendarSynced === false) await syncPersonalLesson(lesson, user);
      }
      setGoogleSyncPending(false);
      triggerAlert(t('alerts.googleAdded'));
    } catch (e) {
      console.error("Google auth error:", e);
      setGoogleSyncPending(true);
      triggerAlert(e.message || t('alerts.googleAuthError'));
    }
    finally { googleSyncRef.current = false; setGoogleSyncing(false); }
  };


  const registerUserProfile = async u => {
    if (!u?.emailVerified) return;
    try { await bookingAction('requestAccess'); }
    catch (error) { console.error('Access request failed:', error.code); }
  };

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    // setPersistence() здесь больше нет: хранилище сессии задано один раз в
    // firebase.js. Повторный вызов на каждом монтировании переселял пользователя
    // между хранилищами и на время переезда оставлял его «разлогиненным».
    const unsub = onAuthStateChanged(auth, (u) => {
      setAuthReady(true);
      firebaseEmailRef.current = u?.email || null;
      if (sessionRef.current !== u?.uid) {
        firebaseEmailRef.current = u?.email || null;
        setIsAuthorized(false); setGoogleSyncPending(false); setCalendarFresh(false);
        sessionRef.current = u?.uid || null;
        scheduleGenerationRef.current++;
        setWallet(null); setPartnerLessons([]); setMyLessons([]); setRecentLessons([]);
        setSelectedSlots([]); setScheduleReady(false); setBookingAccess(null);
        bookingAccessRef.current = null;
      }
      if (u) {
        setUser(u);
        registerUserProfile(u);
        if (u.providerData.some(p => p.providerId === 'google.com')) initGoogleAPI().catch(() => {});
      } else setUser(null);
    });
    return () => unsub();
  }, []);

  // Сессионный кэш согласия привязан к конкретному ученику.
  useEffect(() => {
    rulesAcceptedRef.current = false;
    setRulesModal({ show: false, saving: false });
  }, [user?.email]);

  // Заголовок/язык — отдельно, чтобы смена языка НЕ пересоздавала подписки на уроки.
  useEffect(() => {
    document.title = isEN ? 'Lessons with Xenia' : 'Уроки испанского';
    auth.languageCode = isEN ? 'en' : 'ru';
  }, [isEN]);

  // Полная история залогиненного ученика: читаем ТОЛЬКО его уроки (по email),
  // любой давности. Это его собственные документы — по чтениям недорого.
  useEffect(() => {
    if (!user?.emailVerified) { setMyLessons([]); return; }
    const qMine = query(collection(db, 'lessons'), where('userEmail', '==', user.email));
    const unsub = onSnapshot(qMine, (snap) =>
      setMyLessons(snap.docs.map(d => ({
        ...d.data(), start: new Date(d.data().start), end: new Date(d.data().end), id: d.id,
      }))));
    return () => unsub();
  }, [user?.email, user?.emailVerified]);

  useEffect(() => {
    if (!user?.emailVerified) {
      setBookingAccess(null);
      bookingAccessRef.current = null;
      setBookingBlockedUntil(null);
      return;
    }
    let timer;
    const docRef = doc(db, 'studentAccess', user.email);
    const unsub = onSnapshot(docRef, (snap) => {
      clearTimeout(timer);
      const data = snap.data();
      const enabled = snap.exists() && data.bookingEnabled === true;
      // Временная блокировка отдельного ученика (например, за нарушение правил):
      // действует до указанной даты, дальше доступ восстанавливается сам собой.
      const blockedUntil = data?.blockedUntil ? new Date(data.blockedUntil) : null;
      const isTemporarilyBlocked = blockedUntil && blockedUntil > new Date();
      const hasAccess = enabled && !isTemporarilyBlocked;
      setBookingAccess(hasAccess);
      bookingAccessRef.current = hasAccess;
      setBookingBlockedUntil(isTemporarilyBlocked ? blockedUntil : null);
      if (enabled && isTemporarilyBlocked) {
        const check = () => {
          const remaining = blockedUntil.getTime() - Date.now();
          if (remaining > 0) { timer = setTimeout(check, Math.min(remaining + 1000, 2147483647)); return; }
          setBookingAccess(true); bookingAccessRef.current = true; setBookingBlockedUntil(null);
        };
        timer = setTimeout(check, Math.min(blockedUntil.getTime() - Date.now() + 1000, 2147483647));
      }
    });
    return () => { clearTimeout(timer); unsub(); };
  }, [user?.email, user?.emailVerified]);

  // ── Auth handlers ─────────────────────────────────────────────────────────
  const handleEmailAuth = async () => {
    try {
      if (authMode === 'login') {
        const result = await signInWithEmailAndPassword(auth, email, password);
        if (!result.user.emailVerified) {
          triggerAlert(t('alerts.emailVerify'));
          await signOut(auth);
          return;
        }
        setUser(result.user);
      } else {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(result.user, { displayName: name });
        await sendEmailVerification(result.user);
        triggerAlert(t('alerts.registered'));
        setAuthMode('login');
      }
      setShowModal(false);
    } catch (e) {
      console.error('Email auth error:', e);
      triggerAlert((isEN ? 'Error: ' : 'Ошибка: ') + e.message);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      setUser(result.user);
      setShowModal(false);
      initGoogleAPI().catch(() => {});
    } catch (e) { console.error("Google login error:", e); }
  };

  const handleResetPassword = async () => {
    if (!email) { triggerAlert(t('alerts.enterEmailReset')); return; }
    try {
      await sendPasswordResetEmail(auth, email, { url: 'https://clases-con-xenia.com/', handleCodeInApp: false });
      triggerAlert(t('alerts.resetSent') + email);
    } catch (e) {
      console.error("Reset error:", e);
      triggerAlert((isEN ? 'Error: ' : 'Ошибка: ') + e.message);
    }
  };

  // ── Derived data ──────────────────────────────────────────────────────────
  const myPendingLessons = events.filter(e => e.userEmail === user?.email && !e.paid).length;

  // «Мои уроки»: все будущие + свои НЕоплаченные прошедшие (чтобы долг был на виду).
  const userBookedLessons = events
    .filter(e => e.userEmail === user?.email && (e.start >= nowDate || !e.paid))
    .sort((a, b) => a.start - b.start);
  googleLessonsRef.current = userBookedLessons;
  useEffect(() => {
    if (!user?.email || !bookingAccess || !isAuthorized) return undefined;
    let active = true;
    const retry = async () => {
      if (!active || document.visibilityState === 'hidden' || googleSyncRef.current || bookingSavingRef.current) return;
      googleSyncRef.current = true;
      try {
        await ensureGoogleAccount(user);
        await drainGoogleDeletes(user);
        for (const lesson of googleLessonsRef.current) {
          if (!active) break;
          if (!lesson.googleEventId || lesson.studentCalendarSynced === false) await syncPersonalLesson(lesson, user);
        }
        if (active) setGoogleSyncPending(false);
      } catch { if (active) setGoogleSyncPending(true); }
      finally { googleSyncRef.current = false; }
    };
    googleRetryRef.current = retry;
    retry();
    // Quiet tabs do not read Firestore. Retry a failed sync at most every five minutes.
    const timer = setInterval(() => { if (googleSyncPendingRef.current) retry(); }, 5 * 60000);
    const visibleRetry = () => { if (document.visibilityState === 'visible') retry(); };
    document.addEventListener('visibilitychange', visibleRetry);
    return () => {
      active = false; googleRetryRef.current = null; clearInterval(timer);
      document.removeEventListener('visibilitychange', visibleRetry);
    };
    // Operations check the current account; the lesson list is read from its ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, bookingAccess, isAuthorized]);

  useEffect(() => { googleRetryRef.current?.(); }, [myLessons]);

  // Прошедшие: вся история ученика (грузится запросом по его email выше).
  const userPastLessons = events
    .filter(e => e.userEmail === user?.email && e.start < nowDate)
    .sort((a, b) => b.start - a.start);

  const partnerInfo = getPartnerInfo();

  // Неоплаченные уроки партнёра + общая сумма неоплаченных по паре
  const partnerPendingLessons = partnerInfo
    ? events.filter(e => e.userEmail === partnerInfo.userEmail && !e.paid).length
    : 0;
  const pendingLessons = myPendingLessons + partnerPendingLessons;
  const bookingNeedsPayment = myPendingLessons > 0 || selectedSlots.length - Math.max(0, getSharedBalance()) > 1;
  useEffect(() => { setPaymentReported(false); }, [selectedSlots, user?.uid, myPendingLessons, credits]);

  // В карточке партнёра показываем все будущие уроки + все НЕоплаченные прошедшие,
  // чтобы платящий видел весь долг (совпадает со счётчиком «Не оплачено»).
  const partnerBookedLessons = partnerInfo
    ? events
        .filter(e => e.userEmail === partnerInfo.userEmail && (e.start >= nowDate || !e.paid))
        .sort((a, b) => a.start - b.start)
    : [];

  const partnerPastLessons = partnerInfo
    ? events.filter(e => e.userEmail === partnerInfo.userEmail && e.start < nowDate).sort((a, b) => b.start - a.start)
    : [];

  const stats = (ls) => ({ paid: ls.filter(l => l.paid).length, unpaid: ls.filter(l => !l.paid).length });
  const userPastStats = stats(userPastLessons);
  const partnerPastStats = stats(partnerPastLessons);
  const totalPastLessons = userPastLessons.length + partnerPastLessons.length;

  // ── Sub-components ────────────────────────────────────────────────────────
  const MyEvent = ({ event }) => {
    if (event.temp) return <span>{event.title}</span>;
    if (event.blocked) return <span></span>;
    if (event.userEmail === user?.email)
      return <div style={{ textAlign: "center", lineHeight: 1.2 }}><div style={{ fontWeight: "bold" }}>{event.title}</div></div>;
    return <span></span>;
  };

  const LessonItem = ({ lesson, showDelete = false, isPartner = false }) => (
    <li style={{
      marginBottom: "10px", padding: "12px",
      backgroundColor: isPartner ? "#f5f0ff" : "#f0f8ff",
      borderRadius: "8px", boxShadow: "0px 2px 5px rgba(0,0,0,0.08)",
      display: "flex", justifyContent: "space-between", alignItems: "center",
      borderLeft: `3px solid ${lesson.paid ? '#27ae60' : '#f39c12'}`,
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={{ fontSize: "14px", color: "#2c3e50", fontWeight: '500' }}>
          {format(new Date(lesson.start), "dd MMMM yyyy, HH:mm", { locale: dateLocale })}
        </div>
        <PaymentBadge paid={lesson.paid} isEN={isEN} />
      </div>
      {showDelete && (
        <span onClick={() => handleSlotDelete(lesson)}
          style={{ fontWeight: "bold", fontSize: "20px", color: "#e74c3c", cursor: "pointer", padding: '0 5px' }}>
          ×
        </span>
      )}
    </li>
  );

  const TimeSlotWrapper = useMemo(() => {
    return function({ value, children }) {
      const t = value.getTime();
      const isBlockedBySlot = blockedSlotsRef.current.some(
        slot => t >= slot.start.getTime() && t < slot.end.getTime()
      );
      const isUnavailable = isSlotUnavailable(value, openHoursRef.current);
      const hasNoAccess = !bookingAccessRef.current;
      const blockedStyle = {
        backgroundColor: '#e3e8f0',
        pointerEvents: 'none',
      };
      let bgStyle = {};
      if (hasNoAccess || isUnavailable || isBlockedBySlot) bgStyle = blockedStyle;
      return React.cloneElement(children, { style: { ...children.props.style, ...bgStyle } });
    };
  }, []);

  const PastLessonsSection = ({ lessons, title, stats: s, isPartner = false }) => (
    <div style={{ marginBottom: '15px' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '10px', paddingBottom: '8px',
        borderBottom: `1px solid ${isPartner ? '#e8e0f0' : '#e0e8f0'}`,
      }}>
        <span style={{ fontSize: '14px', fontWeight: '600', color: isPartner ? '#9b59b6' : '#2c3e50' }}>{title}</span>
        <div style={{ display: 'flex', gap: '8px', fontSize: '11px' }}>
          <span style={{ padding: '2px 6px', borderRadius: '10px', backgroundColor: '#d4edda', color: '#155724' }}>✓ {s.paid}</span>
          <span style={{ padding: '2px 6px', borderRadius: '10px', backgroundColor: '#fff3cd', color: '#856404' }}>○ {s.unpaid}</span>
        </div>
      </div>
      {lessons.length > 0 ? (
        <ul style={{ listStyleType: "none", padding: 0, margin: 0 }}>
          {lessons.map((l) => <LessonItem key={l.id} lesson={l} showDelete={false} isPartner={isPartner} />)}
        </ul>
      ) : (
        <p style={{ color: "#7f8c8d", fontSize: "13px", textAlign: "center", margin: '10px 0' }}>{t('lessons.noPast')}</p>
      )}
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="container" style={{
      fontFamily: 'Roboto, sans-serif', backgroundColor: '#f4f6f8',
      padding: '20px', display: 'flex', gap: '20px', flexWrap: 'nowrap',
    }}>
      <CustomAlertModal show={customAlert.show} message={customAlert.message} onClose={closeCustomAlert} okText={t('confirm.ok')} />
      <CustomConfirmModal show={confirmModal.show} message={confirmModal.message} onConfirm={handleConfirmAction} onCancel={closeConfirmModal} yesText={t('confirm.yes')} cancelText={t('confirm.cancel')} />
      {rulesModal.show && (
        <RulesModal
          t={t}
          saving={rulesModal.saving}
          onConfirm={handleRulesConfirm}
          onCancel={() => setRulesModal({ show: false, saving: false })}
        />
      )}

      {/* Левая колонка — календарь */}
      <div style={{ flex: 3, minWidth: '300px', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <header className="header" style={{
          padding: '15px 25px', backgroundColor: '#ffffff', borderRadius: '12px',
          marginBottom: '15px', display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <h1 style={{ color: '#2c3e50', fontSize: '26px', fontWeight: 'bold', margin: 0 }}>Clases con Xenia</h1>
            <span style={{ fontSize: '14px', color: '#7f8c8d', marginTop: '4px', maxWidth: '450px', lineHeight: '1.5' }}>
              {t('header.subtitle')}
            </span>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
              <a href="https://t.me/+393714586672" target="_blank" rel="noopener noreferrer" style={{
                display: 'inline-flex', alignItems: 'center', gap: '5px',
                padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '500',
                backgroundColor: '#0088CC', color: 'white', textDecoration: 'none',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12L7.48 13.911l-2.96-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.668.648z"/>
                </svg>
               Написать мне
              </a>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <a href={isEN ? '/#/' : '/#/en'} style={{
              padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd',
              textDecoration: 'none', fontSize: '14px', color: '#34495e', fontWeight: '500', backgroundColor: '#f8f9fa',
            }}>
              {isEN ? '🇷🇺 RU' : '🇬🇧 EN'}
            </a>
            {!authReady ? null : user ? (
              <>
                <span style={{ fontWeight: 'bold', color: '#34495e', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  👋 Hola, {user.displayName || user.email}
                </span>
                <button onClick={async () => { await signOut(auth); setUser(null); triggerAlert(t('alerts.loggedOut')); }}
                  style={{ padding: '10px 18px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer', fontWeight: 'bold' }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#c0392b'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#e74c3c'}
                >{t('header.logout')}</button>
              </>
            ) : (
              <button onClick={() => setShowModal(true)}
                style={{ padding: '12px 20px', backgroundColor: '#3498db', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer', fontWeight: 'bold' }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#2980b9'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#3498db'}
              >{t('header.login')}</button>
            )}
          </div>
        </header>

        {/* No booking access banner */}
        {user && bookingAccess === false && (
          <div style={{
            marginBottom: '15px', padding: '15px 20px', backgroundColor: '#fff3cd',
            borderRadius: '12px', border: '1px solid #ffeeba', textAlign: 'center',
            color: '#856404', fontSize: '15px', lineHeight: '1.5',
          }}>
            🔒 {bookingBlockedUntil
              ? t('booking.noAccessUntil')(format(bookingBlockedUntil, 'dd MMMM yyyy', { locale: dateLocale }))
              : t('booking.noAccess')}
          </div>
        )}

        {pendingBooking && (
          <div role="status" style={{ padding: '15px', marginBottom: '15px', background: '#fff3cd', borderRadius: '12px' }}>
            <p>{t('alerts.bookingUnconfirmed')}</p>
            <ul>{pendingBooking.data.slots.map(slot => <li key={slot.start}>{format(new Date(slot.start), 'dd MMMM yyyy, HH:mm', { locale: dateLocale })} — {slot.duration} {isEN ? 'min' : 'мин'}</li>)}</ul>
            <button disabled={checkingBooking} onClick={() => recoverBooking(false)}>{t('booking.checkSaved')}</button>{' '}
            <button disabled={checkingBooking} onClick={() => recoverBooking(true)}>{t('booking.retrySame')}</button>
          </div>
        )}

        {/* Booking bar */}
        {user && bookingAccess && (
          <div style={{
            marginBottom: '15px', padding: '15px', backgroundColor: '#fff', borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)', display: 'flex',
            alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontWeight: 'bold', fontSize: '16px' }}>{t('booking.duration')}</label>
              <select value={lessonDuration} onChange={(e) => { setLessonDuration(parseInt(e.target.value)); setSelectedSlots([]); bookingRequestRef.current = null; }}
                style={{ padding: '5px 10px', fontSize: '16px', borderRadius: '6px', border: '1px solid #ccc' }}>
                <option value="45">{t('booking.min45')}</option>
                <option value="60">{t('booking.min60')}</option>
                <option value="90">{t('booking.min90')}</option>
              </select>
            </div>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ fontWeight: 'bold', fontSize: '16px' }}>{t('booking.selected')} {selectedSlots.length}</span>
              <span style={{ cursor: 'pointer', color: '#27ae60', fontSize: '18px', padding: '0 5px' }}
                onMouseEnter={() => setShowSelectedModal(true)} onMouseLeave={() => setShowSelectedModal(false)}>▼</span>
              {showSelectedModal && (
                <div style={{
                  position: 'absolute', top: '30px', right: 0, backgroundColor: '#fff',
                  border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  padding: '10px', zIndex: 100, minWidth: '200px',
                }}>
                  <strong>{t('booking.selectedLessons')}</strong>
                  {selectedSlots.length > 0 ? (
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {selectedSlots.map((slot, idx) => (
                        <li key={idx} style={{ padding: '5px 0', borderBottom: '1px solid #eee' }}>
                          {format(slot.start, 'dd MMMM yyyy, HH:mm', { locale: dateLocale })}
                        </li>
                      ))}
                    </ul>
                  ) : <p style={{ margin: 0 }}>{t('booking.noSelected')}</p>}
                </div>
              )}
            </div>
            {scheduleReady && !calendarFresh && <p>{t('alerts.calendarUpdating')}</p>}
            {bookingNeedsPayment && (
              <div className="booking-payment-note" role="status">
                <strong>🌷 {t('booking.paymentTitle')}</strong>
                <p>{myPendingLessons > 0 ? t('booking.paymentDebt')(myPendingLessons) : t('booking.paymentSelection')}</p>
                {myPendingLessons > 0 && getSharedBalance() > 0 && <button type="button" disabled={bookingSaving || !!pendingBooking} onClick={handlePayFromBalance}>{t('balance.payFromBalance')}</button>}
                <p>{t('booking.paymentDelay')}</p>
                <label className="booking-payment-confirm">
                  <input type="checkbox" checked={paymentReported} disabled={bookingSaving || !!pendingBooking} onChange={e => setPaymentReported(e.target.checked)} />
                  <span>{t('booking.paymentConfirm')}</span>
                </label>
                <small>{paymentReported ? t('booking.paymentThanks') : t('booking.paymentReview')}</small>
              </div>
            )}
            <button onClick={handleBooking} disabled={bookingSaving || !!pendingBooking || !scheduleReady || !calendarFresh || selectedSlots.length === 0 || (bookingNeedsPayment && !paymentReported)} style={{
              padding: '10px 20px',
              backgroundColor: selectedSlots.length === 0 || (bookingNeedsPayment && !paymentReported) ? '#ccc' : '#27ae60',
              color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px',
              cursor: selectedSlots.length === 0 ? 'not-allowed' : 'pointer',
            }}>{t('booking.book')}</button>
          </div>
        )}

        {/* Auth modal */}
        {showModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex',
            justifyContent: 'center', alignItems: 'center', zIndex: 1000,
          }}>
            <div style={{
              backgroundColor: 'white', padding: '40px', borderRadius: '16px', textAlign: 'center',
              boxShadow: '0 12px 24px rgba(0,0,0,0.2)', position: 'relative',
              fontFamily: 'Arial, sans-serif', maxWidth: '300px', width: '100%',
            }}>
              <button onClick={() => setShowModal(false)} style={{ position: 'absolute', top: '10px', right: '15px', background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#e74c3c' }}>×</button>
              <button onClick={handleGoogleLogin} style={{
                width: '100%', padding: '12px 20px', backgroundColor: '#fff', border: '1px solid #ccc',
                borderRadius: '8px', fontSize: '18px', marginBottom: '15px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', color: '#555',
              }}>
                <img src={googleLogo} alt="Google" style={{ width: '24px', height: '24px' }} />
                {t('auth.loginGoogle')}
              </button>
              {!authMode && (
                <button onClick={() => setAuthMode('login')} style={{
                  width: '100%', padding: '12px', backgroundColor: '#2ecc71', color: 'white',
                  border: 'none', borderRadius: '8px', fontSize: '18px', marginBottom: '15px', cursor: 'pointer',
                }}>{t('auth.loginEmail')}</button>
              )}
              {authMode && (
                <>
                  {authMode === 'register' && (
                    <input type="text" placeholder={t('auth.enterName')} value={name} onChange={(e) => setName(e.target.value)}
                      style={{ width: '90%', padding: '10px', margin: '15px 0', borderRadius: '8px', border: '1px solid #ddd', fontSize: '16px' }} />
                  )}
                  <input type="email" placeholder={t('auth.enterEmail')} value={email} onChange={(e) => setEmail(e.target.value)}
                    style={{ width: '90%', padding: '10px', margin: '15px 0', borderRadius: '8px', border: '1px solid #ddd', fontSize: '16px' }} />
                  <input type="password" placeholder={t('auth.enterPassword')} value={password} onChange={(e) => setPassword(e.target.value)}
                    style={{ width: '90%', padding: '10px', margin: '15px 0', borderRadius: '8px', border: '1px solid #ddd', fontSize: '16px' }} />
                  {authMode === 'login' && (
                    <p onClick={handleResetPassword} style={{ color: '#2980b9', cursor: 'pointer', textDecoration: 'underline', fontSize: '14px', marginBottom: '15px' }}>
                      {t('auth.forgotPassword')}
                    </p>
                  )}
                  <button onClick={handleEmailAuth} style={{
                    width: '100%', padding: '12px', backgroundColor: '#27ae60', color: 'white',
                    border: 'none', borderRadius: '8px', fontSize: '18px', marginBottom: '15px', cursor: 'pointer',
                  }}>{authMode === 'login' ? t('auth.login') : t('auth.register')}</button>
                </>
              )}
              {authMode && (
                <p onClick={() => { setAuthMode(authMode === 'login' ? 'register' : 'login'); setName(''); }}
                  style={{ color: '#2980b9', cursor: 'pointer', textDecoration: 'underline', fontSize: '16px' }}>
                  {authMode === 'login' ? t('auth.noAccount') : t('auth.hasAccount')}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Calendar */}
        {user?.emailVerified && bookingAccess ? (scheduleReady ? <Calendar
          localizer={calendarLocalizer}
          events={combinedEvents}
          selectable
          longPressThreshold={150}
          onSelectEvent={(event) => {
            if (event.temp) setSelectedSlots(prev => prev.filter(s => s.start.getTime() !== event.start.getTime()));
            else if (event.blocked) return;
            else if (event.userEmail === user?.email) handleSlotDelete(event);
            else triggerAlert(t('alerts.otherUser'));
          }}
          onSelectSlot={(slotInfo) => {
            if (!bookingAccess || !scheduleReady || bookingSaving) return;
            const slotStart = new Date(slotInfo.start);
            const slotEnd = new Date(slotStart.getTime() + lessonDuration * 60000);
            if (slotStart < new Date()) { triggerAlert(t('alerts.pastBooking')); return; }
            if (bookingWindowEndRef.current && slotEnd.getTime() > bookingWindowEndRef.current) {
              triggerAlert(t('alerts.bookingHorizon')); return;
            }
            const already = selectedSlots.some(slot => slot.start.getTime() === slotStart.getTime());
            if (already) { setSelectedSlots(prev => prev.filter(slot => slot.start.getTime() !== slotStart.getTime())); return; }
            for (let ms = slotStart.getTime(); ms < slotEnd.getTime(); ms += 60000) {
              if (isSlotUnavailable(new Date(ms), openHours)) { triggerAlert(t('alerts.timeUnavailable')); return; }
            }
            const occupied = [...events, ...blockedSlots, ...selectedSlotEvents];
            if (occupied.some(ev => slotStart < new Date(ev.end) && slotEnd > new Date(ev.start))) {
              triggerAlert(t('alerts.alreadyBooked')); return;
            }
            setSelectedSlots(prev => [...prev, { start: slotStart, end: slotEnd }]);
          }}
          startAccessor="start"
          endAccessor="end"
          defaultView={defaultView}
          views={{ day: true, month: true, week: !isMobile }}
          firstDay={1}
          style={{ height: '100%', width: '100%', padding: '15px', backgroundColor: '#ffffff', borderRadius: '12px', boxShadow: '0px 6px 15px rgba(0,0,0,0.1)', marginBottom: '20px' }}
          step={30}
          timeslots={2}
          getNow={() => new Date()}
          culture={isEN ? 'en-US' : 'ru'}
          messages={{
            previous: t('calendar.previous'), next: t('calendar.next'), today: t('calendar.today'),
            month: t('calendar.month'), week: t('calendar.week'), day: t('calendar.day'),
            agenda: t('calendar.agenda'), date: t('calendar.date'), time: t('calendar.time'),
            event: t('calendar.event'), noEventsInRange: t('calendar.noEvents'),
            showMore: t('calendar.showMore'),
          }}
          components={{ event: MyEvent, timeSlotWrapper: TimeSlotWrapper }}
          dayPropGetter={(date) => {
            const iso = moment(date).format('YYYY-MM-DD');
            if (isDayUnavailable(date, openHoursRef.current)) return { style: { backgroundColor: '#e3e8f0' } };
            const isBlocked = blockedSlotsRef.current.some(slot => {
              const slotIso = moment(slot.start).format('YYYY-MM-DD');
              return slotIso === iso && slot.start.getHours() <= 7 && slot.end.getHours() >= 22;
            });
            if (isBlocked) return { style: { backgroundColor: '#e3e8f0' } };
            return {};
          }}
          eventPropGetter={(event) => {
            const now = new Date();
            if (event.temp) return { style: { backgroundColor: '#8e44ad', border: '2px dashed #fff', color: 'white', borderRadius: '6px', padding: '2px' } };
            if (event.blocked) return { className: 'slot-unavailable' };
            if (event.userEmail === user?.email) {
              const past = event.start < now;
              return { style: { backgroundColor: past ? (event.paid ? '#95a5a6' : '#f39c12') : (event.paid ? '#2ecc71' : '#e67e22'), color: 'white', border: 'none', borderRadius: '6px', width: '100%', height: '100%' } };
            }
            return { className: 'slot-unavailable' };
          }}
        /> : <div style={{ padding: 30 }}>
          {typeof scheduleError === 'string' ? scheduleError : scheduleError ? (isEN ? 'Could not load availability.' : 'Не удалось загрузить расписание.') : (isEN ? 'Loading availability…' : 'Загружаем расписание…')}
          {scheduleError && <button onClick={refreshSchedule}>{isEN ? 'Retry' : 'Повторить'}</button>}
        </div>) : authReady && (!user || !user.emailVerified) ? (
          <div style={{ padding: 30 }}>{user ? t('alerts.emailVerify') : t('alerts.pleaseLogin')}</div>
        ) : null}
        <footer style={{ textAlign: "center", fontSize: "10px", opacity: 0.5, padding: "10px", flexShrink: 0, marginTop: "auto" }}>
          <a href="/privacy.html" style={{ textDecoration: "none", color: "inherit" }}>Privacy Policy</a>
          {' · '}
          <a href="https://wa.me/393714586672" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", color: "inherit" }}>WhatsApp</a>
          {' · '}
          <a href="https://t.me/+393714586672" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", color: "inherit" }}>Telegram</a>
        </footer>
      </div>

      {/* Правая колонка */}
      {user && (
        <div style={{ flex: 1, minWidth: '280px', display: 'flex', flexDirection: 'column', gap: '20px', height: 'auto', alignSelf: 'flex-start' }}>

          {/* Mi Pasaporte — только RU */}

          {/* Баланс */}
          <div style={{ padding: '20px', backgroundColor: '#ffffff', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', textAlign: 'center', border: pendingLessons > 0 ? '2px solid #e74c3c' : 'none' }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#2c3e50' }}>{t('balance.title')}</h3>
            {credits ? (
              <div style={{ marginBottom: '10px' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: (credits.purchased - credits.booked) > 0 ? '#27ae60' : '#7f8c8d' }}>
                  {credits.purchased - credits.booked} {t('balance.lessons')}
                </div>
              </div>
            ) : (
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#7f8c8d', marginBottom: '10px' }}>0 {t('balance.lessons')}</div>
            )}
            {pendingLessons > 0 && (
              <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#fdecea', color: '#c0392b', borderRadius: '8px', border: '1px solid #e74c3c', display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
                <div style={{ fontSize: '14px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span>⚠️</span><span>{t('balance.unpaid')} {pendingLessons}</span>
                </div>
                {getSharedBalance() > 0 && myPendingLessons > 0 && (
                  <button onClick={handlePayFromBalance} style={{ padding: '8px 12px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', width: '100%' }}>
                    {t('balance.payFromBalance')} ({Math.min(getSharedBalance(), myPendingLessons)})
                  </button>
                )}
                {getSharedBalance() <= 0 && <div style={{ fontSize: '11px', color: '#c0392b', textAlign: 'center' }}>{t('balance.topUp')}</div>}
              </div>
            )}
          </div>

          {/* Мои уроки */}
          <div style={{ padding: '20px', backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)', maxHeight: '40vh', overflowY: 'auto', minHeight: '50px', display: 'flex', flexDirection: 'column' }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#2c3e50' }}>{t('lessons.my')}</h3>
            {userBookedLessons.length > 0 ? (
              <ul style={{ listStyleType: "none", padding: 0, margin: 0 }}>
                {userBookedLessons.map((l) => <LessonItem key={l.id} lesson={l} showDelete={l.start >= nowDate} />)}
              </ul>
            ) : (
              <p style={{ color: "#7f8c8d", fontSize: "14px", textAlign: "center" }}>{t('lessons.noPlanned')}</p>
            )}
          </div>

          {/* Уроки партнёра — только RU */}
          {!isEN && partnerInfo && partnerBookedLessons.length > 0 && (
            <div style={{ padding: '20px', backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)', maxHeight: '30vh', overflowY: 'auto' }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#9b59b6', fontSize: '16px' }}>
                {t('lessons.partner')} {partnerInfo.userName || 'партнёра'}
              </h3>
              <ul style={{ listStyleType: "none", padding: 0, margin: 0 }}>
                {partnerBookedLessons.map((l) => <LessonItem key={l.id} lesson={l} showDelete={false} isPartner={true} />)}
              </ul>
            </div>
          )}

          {/* История уроков — только RU */}
          {!isEN && (
            <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
              <button onClick={() => setShowPastLessons(!showPastLessons)} style={{
                width: '100%', padding: '15px 20px', backgroundColor: showPastLessons ? '#f8f9fa' : '#ffffff',
                border: 'none', borderBottom: showPastLessons ? '1px solid #eee' : 'none',
                cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '16px', transform: showPastLessons ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease', color: '#7f8c8d' }}>▶</span>
                  <span style={{ fontWeight: '600', color: '#2c3e50', fontSize: '15px' }}>{t('lessons.history')}</span>
                  {totalPastLessons > 0 && (
                    <span style={{ backgroundColor: '#e0e0e0', color: '#666', padding: '2px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: '500' }}>{totalPastLessons}</span>
                  )}
                </div>
                <span style={{ fontSize: '12px', color: '#95a5a6' }}>{showPastLessons ? t('lessons.hide') : t('lessons.show')}</span>
              </button>
              {showPastLessons && (
                <div style={{ padding: '20px', backgroundColor: '#ffffff', maxHeight: '40vh', overflowY: 'auto', minHeight: '50px', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginBottom: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '8px', fontSize: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span style={{ width: '12px', height: '12px', backgroundColor: '#27ae60', borderRadius: '3px' }}></span>
                      <span style={{ color: '#666' }}>{t('lessons.legendPaid')}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span style={{ width: '12px', height: '12px', backgroundColor: '#f39c12', borderRadius: '3px' }}></span>
                      <span style={{ color: '#666' }}>{t('lessons.legendUnpaid')}</span>
                    </div>
                  </div>
                  <PastLessonsSection lessons={userPastLessons} title={t('lessons.myPast')} stats={userPastStats} />
                  {partnerInfo && (
                    <PastLessonsSection
                      lessons={partnerPastLessons}
                      title={`${t('lessons.partner')} ${partnerInfo.userName || 'партнёра'}`}
                      stats={partnerPastStats} isPartner={true}
                    />
                  )}
                  {totalPastLessons === 0 && (
                    <p style={{ color: "#7f8c8d", fontSize: "14px", textAlign: "center", padding: '20px 0' }}>{t('lessons.noPastTotal')}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Google Calendar */}
          {(
            <button disabled={googleSyncing} onClick={handleGoogleAuth} style={{ padding: "10px 15px", backgroundColor: "#f39c12", color: "white", border: "none", borderRadius: "5px", cursor: "pointer", fontSize: "14px" }}>
              {isAuthorized ? t('google.sync') : t('google.addToCalendar')}
            </button>
          )}
          {googleSyncPending && <p>{t('alerts.googlePending')}</p>}

        </div>
      )}
    </div>
  );
}

export default BookingCalendar;
